import { createContext, useContext, useEffect, useState } from 'react';
import { Sidebar } from './Sidebar';
import { Toaster } from 'sonner';

/**
 * GUARDA DE CONTEXTO — O SHELL MONTADO UMA VEZ SÓ
 *
 * O PROBLEMA: o `MainLayout` era montado DENTRO de cada uma das 17 páginas. Ao
 * navegar entre elas, o React desmontava e remontava a árvore inteira, e com ela
 * a `Sidebar`. Como o `<nav>` que rola era um elemento DOM NOVO, ele nascia com
 * `scrollTop 0`: o menu "voltava ao topo" a cada clique. O mesmo motivo fechava
 * o menu mobile, perdia o foco do teclado e refazia o shell a cada navegação.
 *
 * A CORREÇÃO: o shell passa a ser um layout de ROTA (ver `App.js`), montado uma
 * única vez em volta das rotas internas. Este contexto faz as duas pontas
 * conversarem sem precisar mexer nas 17 páginas: quem está dentro do layout de
 * rota recebe `true` e o `MainLayout` da página vira PASSA-DIRETO.
 *
 * Por que assim: mexer em 17 arquivos (e em ~12 saídas antecipadas que repetem
 * o wrapper) seria um refactor grande e arriscado. A guarda resolve a causa com
 * 2 arquivos, não muda nenhum layout e é idempotente — se um dia os wrappers
 * internos forem removidos, isto continua funcionando sem ajuste.
 */
const LayoutContext = createContext(false);

/** No celular o aviso vai para BAIXO: em cima ele cobria o título e o menu. */
function useTelaPequena() {
  const [pequena, setPequena] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const consulta = window.matchMedia('(max-width: 639px)');
    const aplicar = () => setPequena(consulta.matches);
    aplicar();
    if (consulta.addEventListener) consulta.addEventListener('change', aplicar);
    else if (consulta.addListener) consulta.addListener(aplicar);
    return () => {
      if (consulta.removeEventListener) consulta.removeEventListener('change', aplicar);
      else if (consulta.removeListener) consulta.removeListener(aplicar);
    };
  }, []);
  return pequena;
}

export function MainLayout({ children, fullWidth = false }) {
  const telaPequena = useTelaPequena();
  const jaTemShell = useContext(LayoutContext);

  /* Os hooks acima são chamados SEMPRE, antes deste retorno — a ordem dos hooks
     não pode variar entre renderizações. */
  if (jaTemShell) return children;

  return (
    <LayoutContext.Provider value={true}>
      {/* ETAPA 9 — `area-densa`: o shell aplica `--dens: 0.875` (−12,5%) em toda
          a área interna do sistema de uma vez. Quem precisa ficar no tamanho
          original declara `escala-original` na própria raiz. */}
      <div className="area-densa flex min-h-screen bg-[#0A0A0A]">
      <Sidebar />
      {/* `min-w-0` deixa a área principal encolher em vez de ser empurrada por
          tabela larga (a rolagem horizontal fica dentro do próprio card).
          `lg:p-6` (era p-8) devolve 16px em cima e 16px embaixo.
          `pt-16` no celular reserva o espaço do botão do menu — sem ele o ícone
          ficava EM CIMA das primeiras letras do título da página.
          O teto sobe para 1600px em telas muito largas: em 1633px o conteúdo
          usa os 1.329px disponíveis em vez de parar em 1.280px. */}
      {/* ETAPA 8 (densidade) — `lg:p-5` no notebook e `2xl:p-6` nos monitores
          grandes: 24px de margem em cada lado numa janela de 1366px (que já
          perde a sidebar) era moldura demais. A partir de 1536px volta ao
          respiro original. */}
      <main className="flex-1 min-w-0 lg:ml-0 p-4 pt-16 lg:p-5 2xl:p-6 overflow-auto">
        <div className={fullWidth ? 'w-full' : 'mx-auto w-full max-w-7xl 2xl:max-w-[1600px]'}>
          {children}
        </div>
      </main>
      <Toaster 
        position={telaPequena ? 'bottom-center' : 'top-right'} 
        toastOptions={{
          style: {
            background: '#141414',
            border: '1px solid #27272A',
            color: '#fff',
          },
        }}
      />
      </div>
    </LayoutContext.Provider>
  );
}
